import { type FormEvent, useEffect, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import CssBaseline from "@mui/material/CssBaseline";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { toast } from "react-toastify";

import TokenManager from "../../commons/TokenManager";
import { getAuthenticatedRedirectUrl } from "../../commons/helpers";
import { useAuthContext } from "../../contexts/Auth";
import InputConflictValidator, {
    ConflictValidationStatus,
} from "../components/InputConflictValidator";
import useAuth from "../../services/useAuth";
import useUsers from "../../services/useUsers";

const theme = createTheme();
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;

function getPasswordError(value: string) {
    if (!passwordRegex.test(value)) {
        return "A senha deve ter pelo menos 8 caracteres, uma letra maiuscula, uma letra minuscula e um numero.";
    }

    return "";
}

function getConfirmPasswordError(password: string, confirmPassword: string) {
    if (password !== confirmPassword) {
        return "As senhas nao conferem.";
    }

    return "";
}

export default function Register() {
    const navigate = useNavigate();
    const { getAccessToken } = useAuth();
    const { createPublicUser, checkIfUserExists } = useUsers();
    const { isAuthenticated, setIsAuthenticated } = useAuthContext();

    const [fullName, setFullName] = useState("");
    const [email, setEmail] = useState("");
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [emailStatus, setEmailStatus] = useState<ConflictValidationStatus>("idle");
    const [usernameStatus, setUsernameStatus] = useState<ConflictValidationStatus>("idle");
    const [touched, setTouched] = useState({
        password: false,
        confirmPassword: false,
    });
    const [submitAttempted, setSubmitAttempted] = useState(false);

    const passwordError = getPasswordError(password);
    const confirmPasswordError = getConfirmPasswordError(password, confirmPassword);
    const showPasswordError = (submitAttempted || touched.password) && Boolean(passwordError);
    const showConfirmPasswordError =
        (submitAttempted || touched.confirmPassword) && Boolean(confirmPasswordError);

    useEffect(() => {
        if (isAuthenticated) {
            const tokenData = TokenManager.getTokenData();
            window.location.replace(getAuthenticatedRedirectUrl(Boolean(tokenData.validated)));
        }
    }, [isAuthenticated]);

    const validateForm = () => {
        if (!fullName.trim()) {
            toast.error("Nome completo e obrigatorio.");
            return false;
        }

        if (!email.trim()) {
            toast.error("Email e obrigatorio.");
            return false;
        }

        if (!emailRegex.test(email.trim())) {
            toast.error("Informe um email valido.");
            return false;
        }

        if (!username.trim()) {
            toast.error("Username e obrigatorio.");
            return false;
        }

        if (emailStatus === "checking" || usernameStatus === "checking") {
            toast.error("Aguarde a validacao dos campos.");
            return false;
        }

        if (emailStatus === "taken") {
            toast.error("Email ja esta em uso.");
            return false;
        }

        if (usernameStatus === "taken") {
            toast.error("Username ja esta em uso.");
            return false;
        }

        if (passwordError) {
            toast.error(passwordError);
            return false;
        }

        if (confirmPasswordError) {
            toast.error(confirmPasswordError);
            return false;
        }

        return true;
    };

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setSubmitAttempted(true);

        if (!validateForm()) {
            return;
        }

        const result = await createPublicUser({
            fullName: fullName.trim(),
            email: email.trim(),
            username: username.trim(),
            password,
        });

        if (!result) {
            return;
        }

        const credentials = await getAccessToken({
            emailOrUsername: email.trim(),
            password,
        });

        if (!credentials) {
            toast.error(
                "Conta criada, mas nao foi possivel autenticar automaticamente. Faca login."
            );
            navigate("/", { replace: true });
            return;
        }

        TokenManager.setStayConnect(false);
        TokenManager.setAccessToken(credentials.accessToken, credentials.refreshToken);
        setIsAuthenticated(true);
    };

    return (
        <ThemeProvider theme={theme}>
            <Container component="main" maxWidth="xs">
                <CssBaseline />
                <Box
                    sx={{
                        marginTop: 8,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                    }}
                >
                    <Avatar sx={{ m: 1, bgcolor: "blue" }}>
                        <LockOutlinedIcon />
                    </Avatar>
                    <Typography component="h1" variant="h5">
                        Criar conta
                    </Typography>

                    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            label="nome completo"
                            name="fullName"
                            autoComplete="name"
                            autoFocus
                            value={fullName}
                            onChange={(event) => {
                                setFullName(event.target.value);
                            }}
                        />
                        <InputConflictValidator
                            label="email"
                            value={email}
                            onValueChange={setEmail}
                            onValidationStatusChange={setEmailStatus}
                            onCheckConflict={async (value) => {
                                if (!emailRegex.test(value)) {
                                    return;
                                }

                                return await checkIfUserExists({ email: value });
                            }}
                            conflictHelperText="Email ja esta em uso"
                            type="email"
                            required
                            fullWidth
                            textFieldProps={{
                                margin: "normal",
                                autoComplete: "email",
                            }}
                        />
                        <InputConflictValidator
                            label="username"
                            value={username}
                            onValueChange={setUsername}
                            onValidationStatusChange={setUsernameStatus}
                            onCheckConflict={async (value) => {
                                if (!value.trim()) {
                                    return;
                                }

                                return await checkIfUserExists({ username: value });
                            }}
                            conflictHelperText="Username ja esta em uso"
                            required
                            fullWidth
                            textFieldProps={{
                                margin: "normal",
                                autoComplete: "username",
                            }}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            label="senha"
                            type={showPassword ? "text" : "password"}
                            autoComplete="new-password"
                            value={password}
                            onChange={(event) => {
                                setPassword(event.target.value);
                            }}
                            onBlur={() => {
                                setTouched((prevState) => ({
                                    ...prevState,
                                    password: true,
                                }));
                            }}
                            error={showPasswordError}
                            helperText={showPasswordError ? passwordError : ""}
                            InputProps={{
                                endAdornment: (
                                    <IconButton
                                        onClick={() => {
                                            setShowPassword((currentValue) => !currentValue);
                                        }}
                                    >
                                        {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                    </IconButton>
                                ),
                            }}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            label="confirmar senha"
                            type={showConfirmPassword ? "text" : "password"}
                            autoComplete="new-password"
                            value={confirmPassword}
                            onChange={(event) => {
                                setConfirmPassword(event.target.value);
                            }}
                            onBlur={() => {
                                setTouched((prevState) => ({
                                    ...prevState,
                                    confirmPassword: true,
                                }));
                            }}
                            error={showConfirmPasswordError}
                            helperText={showConfirmPasswordError ? confirmPasswordError : ""}
                            InputProps={{
                                endAdornment: (
                                    <IconButton
                                        onClick={() => {
                                            setShowConfirmPassword((currentValue) => !currentValue);
                                        }}
                                    >
                                        {showConfirmPassword ? (
                                            <VisibilityIcon />
                                        ) : (
                                            <VisibilityOffIcon />
                                        )}
                                    </IconButton>
                                ),
                            }}
                        />
                        <Button fullWidth variant="contained" sx={{ mt: 3, mb: 2 }} type="submit">
                            Criar conta
                        </Button>
                        <Link
                            component={RouterLink}
                            to="/"
                            underline="hover"
                            sx={{ display: "block", textAlign: "center" }}
                        >
                            Pagina de login
                        </Link>
                    </Box>
                </Box>
            </Container>
        </ThemeProvider>
    );
}
